import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  buildSharedSessionExistsQueryKey,
  buildSharedSessionsQueryKey,
  targetFromMutationRequest,
} from "./shared-session-cache-utils";
import type { SharedSession } from "./types";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { displayErrorToast } from "@/utils/toast";

export interface DeleteSharedSessionRequest {
  workspace_id: number;
  shared_session_id: string;
  prompt_id?: number;
  workflow_id?: number;
  skill_collection_id?: string;
}

export const useDeleteSharedSession = () => {
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workspace_id, shared_session_id }: DeleteSharedSessionRequest) => {
      const response = await fetch(
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/shared_sessions/${shared_session_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authContext?.userToken}`,
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        let message = "Failed to delete shared session";
        try {
          const errorData = await response.json();
          message = errorData.message || message;
        } catch {
          // non-JSON error body — keep default message
        }
        throw new Error(message);
      }

      return null;
    },

    onSuccess: (
      _data,
      { workspace_id, shared_session_id, prompt_id, workflow_id, skill_collection_id },
    ) => {
      // ── exact keys used by the two read queries ──────────────────────────
      const target = targetFromMutationRequest({
        workspace_id,
        prompt_id,
        workflow_id,
        skill_collection_id,
      });
      const sessionsKey = buildSharedSessionsQueryKey(target);
      const existsKey = buildSharedSessionExistsQueryKey(target);

      // 1. Optimistically remove the deleted session from the list so the UI
      //    updates immediately without waiting for a refetch.
      queryClient.setQueryData<SharedSession[]>(sessionsKey, old =>
        old ? old.filter(s => s.id !== shared_session_id) : [],
      );

      // 2. Read the post-removal list to decide whether any sessions remain.
      const remaining = queryClient.getQueryData<SharedSession[]>(sessionsKey) ?? [];

      // 3. If the list is now empty, flip the exists flag immediately so the
      //    trigger button reverts to "Share" without a round-trip.
      if (remaining.length === 0) {
        queryClient.setQueryData(existsKey, false);
      }

      // 4. Reconcile with the server in the background.
      //    We invalidate each key individually — DO NOT use the prefix
      //    ["shared_sessions", workspace_id] for the exists query because
      //    its key starts with "exists" at position [1], not workspace_id,
      //    so a prefix match on workspace_id would skip it entirely.
      queryClient.invalidateQueries({ queryKey: sessionsKey });
      queryClient.invalidateQueries({ queryKey: existsKey });
    },

    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to delete shared session");
    },
  });
};
